import type { EngravingFont, EngravingFontSize, EngravingLine } from "./types";

// TODO: Source engraving pricing from the backend instead of hardcoding it.
// The arrangement config response already carries an "Engraving of merchandise
// (per item)" offering at $49 (informationalOnly), so this frontend value is
// a duplicate today. Before wiring it up, we need to decide the data model —
// different products could plausibly carry different engraving fees (e.g.,
// metal urn vs. wooden urn vs. keepsake jewelry), and a single flat
// backend-wide engraving price wouldn't cover that. Likely shape is a
// per-product engraving price or per-material tier, exposed in the product
// catalog. Worth a design pass before implementing.
export const ENGRAVING_PRICE = 49;
export const ENGRAVING_MAX_LINES = 3;
export const ENGRAVING_MAX_CHARS = 30;

export const ENGRAVING_FONT_SIZES: {
  value: EngravingFontSize;
  label: string;
}[] = [
  { value: "XS", label: "XS" },
  { value: "S", label: "S" },
  { value: "M", label: "M" },
  { value: "L", label: "L" },
  { value: "XL", label: "XL" },
];

export const ENGRAVING_FONTS: {
  value: EngravingFont;
  label: string;
  fontFamily: string;
  italic?: boolean;
}[] = [
  { value: "Arial", label: "Arial", fontFamily: "Arial, sans-serif" },
  {
    value: "Lucida",
    label: "Lucida",
    fontFamily: "'Lucida Calligraphy', 'Lucida Handwriting', cursive",
    italic: true,
  },
  { value: "Raleway", label: "Raleway", fontFamily: "'Raleway', sans-serif" },
  {
    value: "Raleway Italic",
    label: "Raleway Italic",
    fontFamily: "'Raleway', sans-serif",
    italic: true,
  },
  {
    value: "Noto Serif",
    label: "Noto Serif",
    fontFamily: "'Noto Serif', serif",
  },
  {
    value: "Edwardian Script",
    label: "Edwardian Script",
    fontFamily: "'Edwardian Script ITC', 'Segoe Script', cursive",
  },
  {
    value: "Mission Script",
    label: "Mission Script",
    fontFamily: "'Mission Script', cursive",
    italic: true,
  },
];

// TODO: Download symbol images from Zepto and self-host in public/ to avoid
// reliance on Zepto's CDN which could change or break without notice.
const ZEPTO_CDN =
  "https://cdn-zeptoapps.com/product-personalizer/images/c9778f.myshopify.com";

export type EngravingSymbol = { id: string; name: string; image: string };

export const ENGRAVING_SYMBOLS: EngravingSymbol[] = [
  { id: "Dove", name: "Dove", image: `${ZEPTO_CDN}/Dove-White2.png?v=1234` },
  {
    id: "Cross Filled",
    name: "Cross",
    image: `${ZEPTO_CDN}/Cross-Filled-White2.png?v=1234`,
  },
  {
    id: "Celtic Cross",
    name: "Celtic Cross",
    image: `${ZEPTO_CDN}/Celtic-Cross-White2.png?v=1234`,
  },
  {
    id: "Star of David",
    name: "Star of David",
    image: `${ZEPTO_CDN}/Star-David-White.png?v=1234`,
  },
  { id: "Heart", name: "Heart", image: `${ZEPTO_CDN}/White-Heart.png?v=1234` },
  { id: "Angel", name: "Angel", image: `${ZEPTO_CDN}/Angel-White2.png?v=1234` },
  {
    id: "Praying Hands",
    name: "Praying Hands",
    image: `${ZEPTO_CDN}/Praying-Hands-White2.png?v=1234`,
  },
  {
    id: "Dove Peace",
    name: "Dove of Peace",
    image: `${ZEPTO_CDN}/Dove-Sign-of-Peace-White.png?v=1234`,
  },
  {
    id: "Rose Stem",
    name: "Rose",
    image: `${ZEPTO_CDN}/White-Rose-Stem.png?v=1234`,
  },
  {
    id: "Rose No Stem",
    name: "Rose (no stem)",
    image: `${ZEPTO_CDN}/White-Rose-No-Stem.png?v=1234`,
  },
  {
    id: "Calla Lilly",
    name: "Calla Lily",
    image: `${ZEPTO_CDN}/White-Calla-Lily.png?v=1234`,
  },
  {
    id: "Butterfly",
    name: "Butterfly",
    image: `${ZEPTO_CDN}/White-Butterfly.png?v=1234`,
  },
  {
    id: "Hummingbird",
    name: "Hummingbird",
    image: `${ZEPTO_CDN}/Hummingbird-Lily-White.png?v=1234`,
  },
  { id: "Tree", name: "Tree", image: `${ZEPTO_CDN}/White-Tree.png?v=1234` },
  {
    id: "Mountains",
    name: "Mountains",
    image: `${ZEPTO_CDN}/White-Mountains.png?v=1234`,
  },
  {
    id: "Shamrock",
    name: "Shamrock",
    image: `${ZEPTO_CDN}/Shamrock-White2.png?v=1234`,
  },
  { id: "Wheat", name: "Wheat", image: `${ZEPTO_CDN}/White-Wheat.png?v=1234` },
  {
    id: "Star",
    name: "Star",
    image: `${ZEPTO_CDN}/Star-White2-fffaf2.png?v=1234`,
  },
  {
    id: "Star 6 Point",
    name: "Star (6 point)",
    image: `${ZEPTO_CDN}/Star-6-Point-White2.png?v=1234`,
  },
  {
    id: "US Flag Waving",
    name: "US Flag",
    image: `${ZEPTO_CDN}/US-Flag-Waving-1-.png?v=1234`,
  },
  {
    id: "US Flag",
    name: "US Flag (flat)",
    image: `${ZEPTO_CDN}/US-Flag-White2.png?v=1234`,
  },
  { id: "Eagle", name: "Eagle", image: `${ZEPTO_CDN}/Eagle-White2.png?v=1234` },
  { id: "Elk", name: "Elk", image: `${ZEPTO_CDN}/Elk-White2.png?v=1234` },
  {
    id: "Equestrian",
    name: "Equestrian",
    image: `${ZEPTO_CDN}/Equestrian-White2.png?v=1234`,
  },
  { id: "Fish", name: "Fish", image: `${ZEPTO_CDN}/White-Fish.png?v=1234` },
  {
    id: "Dolphin",
    name: "Dolphin",
    image: `${ZEPTO_CDN}/White-Dolphin.png?v=1234`,
  },
  {
    id: "Fishing Rod",
    name: "Fishing Rod",
    image: `${ZEPTO_CDN}/White-Fishing-Rod.png?v=1234`,
  },
  {
    id: "Teddy Bear",
    name: "Teddy Bear",
    image: `${ZEPTO_CDN}/White-Teddy-Bear.png?v=1234`,
  },
  {
    id: "Cherub Girl",
    name: "Cherub Girl",
    image: `${ZEPTO_CDN}/CherubGirl-White2.png?v=1234`,
  },
  {
    id: "Cherub Boy",
    name: "Cherub Boy",
    image: `${ZEPTO_CDN}/CherubBoy-White2.png?v=1234`,
  },
  {
    id: "Ribbon",
    name: "Ribbon",
    image: `${ZEPTO_CDN}/White-Ribbon.png?v=1234`,
  },
  {
    id: "Wedding Rings",
    name: "Wedding Rings",
    image: `${ZEPTO_CDN}/White-Wedding-Rings.png?v=1234`,
  },
  {
    id: "Treble Clef",
    name: "Treble Clef",
    image: `${ZEPTO_CDN}/White-Treble-Clef.png?v=1234`,
  },
  {
    id: "Guitar",
    name: "Guitar",
    image: `${ZEPTO_CDN}/White-Electric-Guitar.png?v=1234`,
  },
  {
    id: "Masons",
    name: "Masons",
    image: `${ZEPTO_CDN}/Masons-White2.png?v=1234`,
  },
  {
    id: "Caduceus",
    name: "Caduceus",
    image: `${ZEPTO_CDN}/Caduceus-White2.png?v=1234`,
  },
  {
    id: "Baseball",
    name: "Baseball",
    image: `${ZEPTO_CDN}/Baseball-White2.png?v=1234`,
  },
  {
    id: "Basketball",
    name: "Basketball",
    image: `${ZEPTO_CDN}/White-Basketball.png?v=1234`,
  },
  {
    id: "Football",
    name: "Football",
    image: `${ZEPTO_CDN}/Football-White2.png?v=1234`,
  },
  {
    id: "Soccerball",
    name: "Soccer Ball",
    image: `${ZEPTO_CDN}/White-Soccer-Ball.png?v=1234`,
  },
  {
    id: "Bowling",
    name: "Bowling",
    image: `${ZEPTO_CDN}/Bowling-pins-ball-White2.png?v=1234`,
  },
  {
    id: "White-Bow-and-Arrow",
    name: "Bow & Arrow",
    image: `${ZEPTO_CDN}/White-Bow-and-Arrow.png?v=1234`,
  },
  {
    id: "Antlers",
    name: "Antlers",
    image: `${ZEPTO_CDN}/White-Antlers.png?v=1234`,
  },
];

export const DEFAULT_ENGRAVING_LINE: EngravingLine = {
  text: "",
  fontSize: "M",
};

export function createDefaultEngraving() {
  return {
    lines: [
      { text: "", fontSize: "L" as EngravingFontSize },
      { text: "", fontSize: "S" as EngravingFontSize },
      { text: "", fontSize: "S" as EngravingFontSize },
    ],
    font: "Arial" as EngravingFont,
    symbolId: null,
  };
}
